import { TableColumnType } from '@ui';
import { Case, CaseAccessTicketItem, CaseUserGroup } from '@/api/apis/case';

export type CaseAccessTabProps = {
  caseInfo: Case;
};

export enum CaseAccessRoleType {
  OMS = 'oms',
  CommercialAdoption = 'commercialAdoption',
}

export const CASE_ACCESS_ROLE_LABELS = {
  [CaseAccessRoleType.OMS]: 'OMS',
  [CaseAccessRoleType.CommercialAdoption]: 'Commercial Adoption',
};

const OMS_ROLE_VALUES = ['oms', 'organmanagementspecialist', 'organmanagementspecialists'];
const COMMERCIAL_ADOPTION_ROLE_VALUES = ['commercialadoption'];

const normalizeRole = (role: string) => role.replace(/[\s_-]/g, '').toLowerCase();

const hasRole = (roles: string[] | null, roleValues: string[]) =>
  roles?.some((role) => roleValues.includes(normalizeRole(role))) ?? false;

export const getCaseAccessRoleTypes = (item: CaseAccessTicketItem): CaseAccessRoleType[] => {
  const { forUser, caseRoles } = item;

  const hasOmsRole =
    hasRole(forUser.roles, OMS_ROLE_VALUES) ||
    Boolean(caseRoles?.some((role) => role.group === CaseUserGroup.OrganManagementSpecialists));

  const hasCommercialAdoptionRole = hasRole(forUser.roles, COMMERCIAL_ADOPTION_ROLE_VALUES);

  const roles: CaseAccessRoleType[] = [];

  if (hasOmsRole) {
    roles.push(CaseAccessRoleType.OMS);
  }

  if (hasCommercialAdoptionRole) {
    roles.push(CaseAccessRoleType.CommercialAdoption);
  }

  return roles.length ? roles : [CaseAccessRoleType.OMS];
};

export const getCaseAccessRoleLabel = (item: CaseAccessTicketItem) =>
  getCaseAccessRoleTypes(item)
    .map((role) => CASE_ACCESS_ROLE_LABELS[role])
    .join(' , ');

export const getUserDisplayName = ({ firstName, lastName, email, id }: CaseAccessTicketItem['forUser']) => {
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  return fullName || email || id;
};

export const getAccessStatusLabel = (status: CaseAccessTicketItem['status']) =>
  status === 'NotFound' ? 'No access' : status;

export const DEFAULT_ITEMS_PER_PAGE = 10;

export const accessColumns = {
  name: {
    key: 'name',
    label: 'Name',
    isSortable: true,
    minWidthClassName: 'max-w-[210px]',
  },
  userRole: {
    key: 'userRole',
    label: 'User Role',
    isSortable: true,
    minWidthClassName: 'max-w-[170px]',
  },
  roleOnCase: {
    key: 'roleOnCase',
    label: 'Role on case',
    isSortable: false,
    minWidthClassName: 'max-w-[112px]',
  },
  accessStatus: {
    key: 'accessStatus',
    label: 'Access',
    isSortable: false,
    minWidthClassName: 'max-w-[111px]',
  },
  accessExpirationDate: {
    key: 'accessExpirationDate',
    label: 'Expiry',
    isSortable: true,
    minWidthClassName: 'max-w-[180px]',
  },
  grantAccess: {
    key: 'grantAccess',
    label: 'Grant access',
    isSortable: false,
    minWidthClassName: 'max-w-[121px]',
  },
};

export const CASE_ACCESS_COLUMNS: TableColumnType[] = [
  accessColumns.name,
  accessColumns.userRole,
  accessColumns.roleOnCase,
  accessColumns.accessStatus,
  accessColumns.accessExpirationDate,
  accessColumns.grantAccess,
];
